import {
    RequestService,
    RequestServiceResponse,
} from '@tcc-online/request-service';
import CriterionType from '../types/criteria/CriterionType';
import CriterionDisplayOption from '../types/criteria/CriterionDisplayOption';
import IID from '../../types/IID';
import CriterionValueType from '../types/criteria/CriterionValueType';
import AdminProjectTypes from '../constants/AdminProjectTypes';
import ImportService from './ImportService';
import { isSuccessfulResponse } from '../../helpers';

const AdminSortingCriteriaService = {
    getCriteria,
    getCriterion,
    updateCriterion,
    createCriterion,
    deleteCriterion,
    getValues,
    createValue,
    updateValue,
    deleteValue,
    importCriteria,
    exportCriteria,
    findCriterionById,
    flattenCriteria,
};

export interface BasePayload {
    projectId: number;
}

export interface ParentValueID {
    parentCriterionValueId?: number;
}

interface ParentID {
    parentCriterionId?: number;
}

interface URLParams extends BasePayload, ParentValueID {
    criterionId?: number;
}

const createUrl = (params: URLParams): string => {
    const { projectId, criterionId, parentCriterionValueId } = params;
    let result: string = `/admin/products/${AdminProjectTypes.Survey}/projects/${projectId}/criteria`;

    if (criterionId) {
        result += `/${criterionId}`;
    }

    if (parentCriterionValueId) {
        result += `?parentCriterionValueId=${parentCriterionValueId}`;
    }

    return result;
};

interface URLValueParams {
    projectId: number;
    criterionId: number;
    valueId?: number;
    parentCriterionValueId?: number;
}

const createValueUrl = (params: URLValueParams) => {
    const { projectId, criterionId, valueId, parentCriterionValueId } = params;

    let result: string = createUrl({ projectId, criterionId }) + '/values';

    if (valueId) {
        result += `/${valueId}`;
    }

    if (parentCriterionValueId) {
        result += `?parentCriterionValueId=${parentCriterionValueId}`;
    }

    return result;
};

async function getCriteria(
    payload: BasePayload & { locale?: string; limit?: number },
): Promise<RequestServiceResponse<CriterionType[]>> {
    const { projectId, locale, limit } = payload;
    return await RequestService.get<CriterionType[]>(
        createUrl({ projectId }),
        limit ? { limit } : {},
        locale ? { headers: { Language: locale } } : {},
    );
}

async function getCriterion(
    payload: BasePayload & IID,
): Promise<RequestServiceResponse<CriterionType>> {
    const { projectId, id } = payload;
    return await RequestService.get<CriterionType>(
        createUrl({ projectId, criterionId: id }),
    );
}

interface BaseUpdatePayload {
    order?: number;
    displayOption?: CriterionDisplayOption;
    name?: string;
    mandatory?: boolean;
}

export interface UpdatePayload
    extends BaseUpdatePayload, BasePayload, IID, ParentValueID, ParentID {}

async function updateCriterion(
    payload: UpdatePayload,
): Promise<RequestServiceResponse<CriterionType>> {
    const { projectId, id, order, displayOption, name } = payload;
    return await RequestService.patch<BaseUpdatePayload, CriterionType>(
        createUrl({ projectId, criterionId: id }),
        { order, displayOption, name },
    );
}

interface BaseCreatePayload {
    displayOption: CriterionDisplayOption;
    name: string;
    mandatory: boolean;
}

export interface CreatePayload
    extends BaseCreatePayload, BasePayload, ParentValueID, ParentID {
    parentCriterionValue?: CriterionValueType;
}

async function createCriterion(
    payload: CreatePayload,
): Promise<RequestServiceResponse<CriterionType>> {
    const {
        projectId,
        displayOption,
        name,
        mandatory,
        parentCriterionValueId,
    } = payload;
    return await RequestService.post<BaseCreatePayload, CriterionType>(
        createUrl({ projectId, parentCriterionValueId }),
        { displayOption, name, mandatory },
    );
}

export interface DeletePayload extends BasePayload, IID, ParentID {}

async function deleteCriterion(
    payload: DeletePayload,
): Promise<RequestServiceResponse<CriterionType>> {
    const { projectId, id } = payload;
    return await RequestService.delete<CriterionType>(
        createUrl({ projectId, criterionId: id }),
    );
}

export interface BaseValuePayload extends BasePayload {
    criterionId: number;
}

export interface CreateValuePayload {
    value: string;
}

async function createValue(
    payload: CreateValuePayload &
        BaseValuePayload & { parentCriterionValueId?: number },
): Promise<RequestServiceResponse<CriterionValueType>> {
    const { projectId, criterionId, value, parentCriterionValueId } = payload;
    return await RequestService.post<CreateValuePayload, CriterionValueType>(
        createValueUrl({ projectId, criterionId, parentCriterionValueId }),
        {
            value,
        },
    );
}

async function getValues(
    payload: ParentValueID & BaseValuePayload,
): Promise<RequestServiceResponse<CriterionValueType[]>> {
    const { projectId, criterionId, parentCriterionValueId } = payload;
    return await RequestService.get<CriterionValueType[]>(
        createValueUrl({ projectId, criterionId, parentCriterionValueId }),
    );
}

interface BaseUpdateValuePayload {
    value?: string;
    order?: number;
}

export interface UpdateValuePayload
    extends BaseUpdateValuePayload, BaseValuePayload, IID, ParentID {}

async function updateValue(
    payload: UpdateValuePayload,
): Promise<RequestServiceResponse<CriterionValueType>> {
    const { projectId, criterionId, id, value, order } = payload;
    return await RequestService.patch<
        BaseUpdateValuePayload,
        CriterionValueType
    >(createValueUrl({ projectId, criterionId, valueId: id }), {
        value,
        order,
    });
}

async function deleteValue(
    payload: BaseValuePayload & IID,
): Promise<RequestServiceResponse<unknown>> {
    const { projectId, criterionId, id } = payload;
    return await RequestService.delete(
        createValueUrl({ projectId, criterionId, valueId: id }),
    );
}

async function importCriteria(payload: { projectId: number; data: FormData }) {
    const { projectId, data } = payload;

    const result = await RequestService.post<FormData, { importId: number }>(
        `${createUrl({ projectId })}/import`,
        data,
        {
            headers: { 'Content-Type': 'multipart/form-data' },
        },
    );

    if (isSuccessfulResponse(result.status)) {
        ImportService.saveImportId(result.data.importId);
    }

    return result;
}

async function exportCriteria(projectId: number) {
    return await RequestService.get(
        `${createUrl({ projectId })}/export`,
        {},
        { responseType: 'blob' },
    );
}

function findCriterionById(
    criteria: CriterionType[],
    id: number,
    parentCriterionValueId?: number,
): CriterionType | undefined {
    return find(criteria, id, undefined, undefined, parentCriterionValueId);

    function find(
        criteriaList: CriterionType[],
        targetId: number,
        result: CriterionType | undefined,
        parent: CriterionValueType | undefined,
        parentValueId: number | undefined,
    ): CriterionType | undefined {
        for (let index = 0; index < criteriaList.length; index++) {
            if (result) {
                break;
            }

            const criterion = criteriaList[index];
            if (parentValueId !== undefined && parent) {
                if (parentValueId === parent.id && criterion.id === targetId) {
                    return criterion;
                }
            } else if (criterion.id === targetId) {
                return criterion;
            }

            if (!criterion?.values?.length) {
                continue;
            }

            for (const value of criterion.values) {
                if (result) {
                    break;
                }

                if (!value?.criteria?.length) {
                    continue;
                }

                result = find(
                    value.criteria,
                    targetId,
                    result,
                    value,
                    parentValueId,
                );
            }
        }

        return result;
    }
}

function flattenCriteria(criteria: CriterionType[]): CriterionType[] {
    const result = new Map<number, CriterionType>();

    iterate(criteria);

    return Array.from(result.values());

    function iterate(criteriaList?: CriterionType[]) {
        if (!criteriaList?.length) {
            return;
        }

        for (const criterion of criteriaList) {
            if (!criterion?.values?.length) {
                continue;
            }

            if (!result.has(criterion.id)) {
                result.set(criterion.id, criterion);
            }

            for (const value of criterion.values) {
                if (!value?.criteria?.length) {
                    continue;
                }

                iterate(value.criteria);
            }
        }
    }
}

export default AdminSortingCriteriaService;
